import {
  createMutationKeys,
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory'
import { APIAxiosInstance, RequestError } from '@core/utils/api-axios-instance'
import { RequestTypes } from './requestTypes'
import { createQueryKeyString } from '@core/react-query/queryParams'
import { localizedStrings } from '@core/strings'

const templateQueryKeys = createQueryKeys('register-templates', {
  byOrganisation: ({
    organisationId,
    ...rest
  }: RequestTypes['getRegisterTemplates']) => {
    return {
      queryKey: [organisationId, rest],
      queryFn: async () => {
        const queryString = createQueryKeyString({
          organisationId,
          ...rest,
        })

        const { data } = await APIAxiosInstance.get(
          `registers/templates${queryString}`,
          {},
        )
        return data
      },
      contextQueries: {
        byTemplateId: ({ templateId }: RequestTypes['getRegisterTemplate']) => {
          return {
            queryKey: [templateId],
            queryFn: async () => {
              const { data } = await APIAxiosInstance.get(
                `registers/templates/${templateId}`,
                {},
              )
              return data
            },
          }
        },
      },
    }
  },
  byOrganisationLive: ({
    organisationId,
    term,
  }: RequestTypes['getRegisterTemplatesLive']) => {
    const queryString = createQueryKeyString({
      organisationId,
      term,
    })
    return {
      queryKey: [organisationId, term],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `registers/templates/live${queryString}`,
          {},
        )
        return data
      },
    }
  },
})

const templateMutationKeys = createMutationKeys('register-templates', {
  createRegisterTemplate: {
    mutationKey: null,
    mutationFn: async (payload: RequestTypes['createRegisterTemplate']) => {
      const queryString = createQueryKeyString({
        organisationId: payload.organisationId,
      })
      const { data } = await APIAxiosInstance.post(
        `registers/templates${queryString}`,
        {
          ...payload,
        },
      )

      return data
    },
  },
  updateRegisterTemplate: {
    mutationKey: null,
    mutationFn: async ({
      id,
      ...rest
    }: RequestTypes['updateRegisterTemplate']) => {
      const { data } = await APIAxiosInstance.put(`registers/templates/${id}`, {
        ...rest,
      })

      return data
    },
  },
  updateRegisterTemplateStatus: {
    mutationKey: null,
    mutationFn: async ({
      id,
      ...rest
    }: RequestTypes['updateRegisterTemplateStatus']) => {
      const { data } = await APIAxiosInstance.put(
        `registers/templates/${id}/status`,
        {
          ...rest,
        },
      )

      return data
    },
  },
  recalculateRegisterTemplateFormulas: {
    mutationKey: null,
    mutationFn: async ({
      id,
    }: RequestTypes['recalculateRegisterTemplateFormulas']) => {
      const { data } = await APIAxiosInstance.put(
        `registers/templates/${id}/recalculate-formulas`,
        {},
      )

      return data
    },
  },
  createRegisterTemplateProperty: {
    mutationKey: null,
    mutationFn: async ({
      templateId,
      ...rest
    }: RequestTypes['createRegisterTemplateProperty']) => {
      const { data } = await APIAxiosInstance.post(
        `registers/templates/${templateId}/properties`,
        {
          ...rest,
        },
      )

      return data
    },
  },
  updateRegisterTemplateProperty: {
    mutationKey: null,
    mutationFn: async ({
      templateId,
      id,
      ...rest
    }: RequestTypes['updateRegisterTemplateProperty']) => {
      const { data } = await APIAxiosInstance.put(
        `registers/templates/${templateId}/properties/${id}`,
        {
          ...rest,
        },
      )

      return data
    },
  },
  deleteRegisterTemplateProperty: {
    mutationKey: null,
    mutationFn: async ({
      templateId,
      id,
      ...rest
    }: RequestTypes['deleteRegisterTemplateProperty']) => {
      const { data } = await APIAxiosInstance.delete(
        `registers/templates/${templateId}/properties/${id}`,
        {
          ...rest,
        },
      )

      return data
    },
  },
  updateRegisterTemplatePropertyOrder: {
    mutationKey: null,
    mutationFn: async ({
      templateId,
      ...rest
    }: RequestTypes['updateRegisterTemplatePropertyOrder']) => {
      const { data } = await APIAxiosInstance.post(
        `registers/templates/${templateId}/properties/change-order`,
        {
          ...rest,
        },
      )

      return data
    },
  },
  downloadRegisterTemplateCsv: {
    mutationKey: null,
    mutationFn: async ({
      templateId,
      ...rest
    }: RequestTypes['downloadRegisterTemplateCsv']) => {
      const { data } = await APIAxiosInstance.post(
        `registers/templates/${templateId}/download/csv`,
        {
          ...rest,
        },
      )

      return data
    },
  },
  createRegisterTemplateDiagram: {
    mutationKey: null,
    mutationFn: async ({
      templateId,
      ...rest
    }: RequestTypes['createRegisterTemplateDiagram']) => {
      const { data } = await APIAxiosInstance.post(
        `registers/templates/${templateId}/diagrams`,
        rest,
      )

      return data
    },
  },
  updateRegisterTemplateDiagram: {
    mutationKey: null,
    mutationFn: async ({
      templateId,
      diagramId,
      ...rest
    }: RequestTypes['updateRegisterTemplateDiagram']) => {
      const { data } = await APIAxiosInstance.put(
        `registers/templates/${templateId}/diagrams/${diagramId}`,
        rest,
      )

      return data
    },
  },
  importRegisterTemplateProperties: {
    mutationKey: null,
    mutationFn: async ({
      templateId,
      propertiesText,
    }: RequestTypes['importRegisterTemplateProperties']) => {
      const parseProperties = (): { registerProperties: unknown[] } => {
        try {
          const parsed = JSON.parse(propertiesText)
          if (!Array.isArray(parsed?.registerProperties)) {
            throw new Error()
          }
          return parsed
        } catch {
          throw new RequestError(
            localizedStrings.registerPropertiesImportInvalidJson,
            null,
            false,
          )
        }
      }

      const { data } = await APIAxiosInstance.post(
        `registers/templates/${templateId}/import`,
        parseProperties(),
      )

      return data
    },
  },
  exportRegisterTemplateProperties: {
    mutationKey: null,
    mutationFn: async ({
      templateId,
    }: RequestTypes['exportRegisterTemplateProperties']) => {
      const { data } = await APIAxiosInstance.get(
        `registers/templates/${templateId}/export`,
      )

      return data
    },
  },
})

const registerTemplateKeys = mergeQueryKeys(
  templateQueryKeys,
  templateMutationKeys,
)['register-templates']

export { registerTemplateKeys }
